import { ReactNode } from 'react';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Empty from 'antd/lib/empty';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import CheckCircleFilled from '@ant-design/icons/lib/icons/CheckCircleFilled';
import EditOutlined from '@ant-design/icons/lib/icons/EditOutlined';
import UnorderedListOutlined from '@ant-design/icons/lib/icons/UnorderedListOutlined';
import { PRIMARY_COLOR, SECONDARY_COLOR } from 'src/theme';
import { defaultDateFormat } from 'src/domain/common/dateFormatters';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { BillProcessingStatus, ICustomerUtilityBill, displayRateClass, isBillEditable } from 'src/domain/customer-utility-bill/interface';
import { BillStatusTag } from '../../BillStatusTag';
import { EMPTY_VALUE } from '../../billStyles';
import { BillReadOnlyField } from '../../edit-bill/BillReadOnlyField';

const { Text } = Typography;

// Matches the "Billing Period" column format in the main bills table (billColumns.tsx).
const BILL_DATE_FORMAT = 'MMM d, yyyy';

const formatBillDate = (value: string | null) => (value ? defaultDateFormat(new Date(value), EMPTY_VALUE, BILL_DATE_FORMAT) : EMPTY_VALUE);

const billingPeriod = (bill: ICustomerUtilityBill) =>
    bill.billingPeriodStart ? `${formatBillDate(bill.billingPeriodStart)} → ${formatBillDate(bill.billingPeriodEnd)}` : null;

const billFields = (bill: ICustomerUtilityBill): [string, ReactNode][] => [
    ['STORE #', bill.siteNumber],
    ['ACCOUNT #', bill.accountNumber],
    ['METER #', bill.meterNumber],
    ['ENROLLMENT ID (SAN)', bill.enrollmentId],
    ['SERVICE ADDRESS', [bill.address, bill.city, bill.state].filter(Boolean).join(', ')],
    ['UTILITY', bill.utility],
    ['RATE CLASS', displayRateClass(bill.rateClass)],
    ['ZIP CODE', bill.zipCode],
    ['BILLING PERIOD', billingPeriod(bill)],
    ['BILLING KW', bill.billingKw == null ? null : String(bill.billingKw)],
    ['BILLING KWH', bill.billingKwh == null ? null : bill.billingKwh.toLocaleString()],
    ['VERIFIED', bill.verifiedAt ? getComputerFormattedTime(bill.verifiedAt) : null],
];

interface Props {
    bills: ICustomerUtilityBill[];
    onEditBill: (bill: ICustomerUtilityBill) => void;
}

// Overview of the customer utility bills parsed from this file: per-status "n / total" chips, then one collapsible
// panel per bill (key fields in the header, details inside) with an Edit button that opens the bill edit modal.
export const BillFileBillsSection = ({ bills, onEditBill }: Props) => {
    const total = bills.length;
    const statusCounts = bills.reduce<Partial<Record<BillProcessingStatus, number>>>((acc, bill) => {
        acc[bill.processingStatus] = (acc[bill.processingStatus] ?? 0) + 1;
        return acc;
    }, {});
    const verifiedCount = bills.filter(bill => !!bill.verifiedAt).length;

    const items = bills.map(bill => ({
        key: bill.id,
        label: (
            <Space size={8} wrap>
                <Text strong>{bill.siteName || 'Customer utility bill'}</Text>
                {bill.siteNumber && <Text type="secondary">Store #{bill.siteNumber}</Text>}
                <BillStatusTag status={bill.processingStatus} />
                {bill.verifiedAt && (
                    <Tooltip title={`Verified ${getComputerFormattedTime(bill.verifiedAt)}`}>
                        <CheckCircleFilled style={{ color: PRIMARY_COLOR }} />
                    </Tooltip>
                )}
            </Space>
        ),
        extra: (
            <Button
                size="small"
                icon={<EditOutlined />}
                onClick={event => {
                    // Don't toggle the panel when opening the edit modal.
                    event.stopPropagation();
                    onEditBill(bill);
                }}
            >
                {isBillEditable(bill) ? 'Edit' : 'View'}
            </Button>
        ),
        children: (
            <Row gutter={[20, 10]}>
                {billFields(bill).map(([label, value]) => (
                    <BillReadOnlyField key={label} label={label} value={value} />
                ))}
            </Row>
        ),
    }));

    return (
        <div style={{ marginBottom: 14 }}>
            <Space style={{ display: 'flex', marginBottom: 10 }}>
                <UnorderedListOutlined style={{ color: SECONDARY_COLOR }} />
                <Text strong>Customer Utility Bills ({total})</Text>
            </Space>

            {total === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No bills have been created from this file yet" />
            ) : (
                <>
                    <Space wrap size={[6, 6]} style={{ display: 'flex', marginBottom: 10 }}>
                        {(Object.entries(statusCounts) as [BillProcessingStatus, number][]).map(([status, count]) => (
                            <BillStatusTag key={status} status={status} suffix={` ${count} / ${total}`} />
                        ))}
                        <Tag icon={<CheckCircleFilled />} color="blue" style={{ margin: 0 }}>
                            Verified {verifiedCount} / {total}
                        </Tag>
                    </Space>

                    <Collapse size="small" items={items} />
                </>
            )}
        </div>
    );
};
